// CR-20260508-001 — SessionSyncOverlayService tunables.
// Hidden-tab → return → "in sync" detection windows. Treat as configuration;
// flip values here to retune without touching state-machine logic.

// Visibility detection cadence (Option B polling fallback).
// Matches drift-detection's SAMPLE_INTERVAL_MS so a future merge of the two
// services would not change cost.
export const VISIBILITY_POLL_MS = 1000;

// Probe-loop cadence after returning to visible.
// Faster than visibility poll because the probes are the user-perceived
// latency: every PROBE_INTERVAL_MS is a chance to clear the overlay.
export const PROBE_INTERVAL_MS = 500;

// Probe A — server-time freshness threshold. ServerTimeMessageDto is
// broadcast at ~1 Hz (DefaultDateTimeProvider, ets.platform); 5 s allows
// a few missed messages before the probe fails.
export const SERVER_TIME_TOLERANCE_MS = 5000;

// Probe B — last-quote freshness threshold for SPX + active ES front-month.
// User-confirmed window 10–20 s; chose the middle.
export const QUOTE_FRESHNESS_TOLERANCE_MS = 15000;

// Pathological-case force-logout. If neither probe ever passes and no other
// kickout fires, publish AuthTokenExpiredUIMessage(source=SessionSyncStaleTimeout)
// after this duration.
// LEAK-1 — tightened 60s → 30s. With host 20s kill removed and ESS recovery
// extended to 90s, this is the primary user-facing kickout for "tab returned
// but server-side sync never recovers" (probes failing). 30s is short enough
// to not feel sticky on a truly-dead session.
export const STALE_OVERLAY_KICKOUT_MS = 30000;

// Minimum overlay-visible time AFTER tab returns to visible. Anti-flicker
// AND mask-browser-repaint: heavy webtrader pages take ~hundreds of ms to
// repaint after tab-show; overlay must outlast that window so the user
// sees overlay rather than frozen pre-hide frame.
//
// Anchored to onVisible() (tab-return), NOT onHidden() (hide event) — see
// session-sync-overlay.service.ts for the re-anchor in onVisible.
export const MIN_OVERLAY_VISIBLE_MS = 750;

// CR-20260508-001 iter 24 — overlay show debounce on tab-hide. Aligned
// with PAUSE_DEBOUNCE_MS in ess-worker so rapid alt-tabs (<7s) are
// completely invisible across all layers: no wire Pause-Send AND no
// overlay flash. Hides shorter than this window never show the overlay
// at all.
//
// For hides ≥ this window: timer fires while tab still hidden, overlay
// becomes visible inside the DOM (user can't see until return), and on
// return the probe loop gates clearance against backend sync state.
export const HIDE_DEBOUNCE_MS = 10000;

// Throttling for ongoing-failure probe logs. Probe loop is 500 ms; logging
// every failure produces ~120 lines/min. Log only every Nth tick when state
// stays the same.
export const PROBE_FAIL_LOG_EVERY_NTH = 5;

// Heartbeat for visibility-poll diagnostics — emits a one-line summary every
// N polls when no transition was observed. Lets us confirm the timer is alive
// without spamming on every tick.
export const VISIBILITY_HEARTBEAT_EVERY_NTH = 60;

export type SessionSyncState =
   | 'IDLE'
   | 'SYNCING'      // tab hidden; overlay visible; waiting for return
   | 'PROBING'      // tab returned; running probes; overlay still visible
   | 'KICKED_OUT';  // terminal latch — cleared only by reset()

export interface SessionSyncDiagnosticStats {
   visibilityEventsFired: number;
   visibilityPollsFired: number;
   transitions: number;
   firstDetectionWinsByEvent: number;
   firstDetectionWinsByPoll: number;
   probeAttempts: number;
   probeAFailures: number;
   probeBFailures: number;
}
